import { PRODUCT_LABELS, VALID_REPORT_TYPES, type ProductCode } from './dataset-utils.ts';

/**
 * Pure filtering/search/count logic shared by the Reports Home dashboard and
 * the Browse All listing. Operates on a structural report shape (satisfied by
 * the ReportsExtension resource) so it stays unit-testable with plain objects.
 */
export interface FilterableReport {
  /** null only for unsaved records (WarpDrive), which never reach a listing. */
  id: string | null;
  name: string;
  description?: string;
  reportType: string;
  createdBy?: number | string;
  isPrivate: boolean;
  isGlobal: boolean;
  isPartnerShared: boolean;
  isPublished: boolean;
  isUnpublished: boolean;
  isArchived: boolean;
  /** SearchConfig-shaped; companyId is read through its index signature. */
  searchConfig?: { [key: string]: unknown };
  metadata?: { publishedCompanyId?: string | null };
}

/** A Browse-rail selection; serialized into the `rail` query param. */
export type RailSelection =
  | { kind: 'all' }
  | { kind: 'mine' }
  | { kind: 'archived' }
  | { kind: 'scope'; scope: 'global' | 'custom' }
  | { kind: 'product'; product: string }
  | { kind: 'company'; companyId: string }
  | { kind: 'folder'; folderId: string };

export type PublishFilter = 'all' | 'published' | 'unpublished';

/** Parse a `rail` query-param value; anything unrecognized falls back to All Reports. */
export function parseRail(raw: string | undefined | null): RailSelection {
  if (!raw || raw === 'all') return { kind: 'all' };
  if (raw === 'mine') return { kind: 'mine' };
  if (raw === 'archived') return { kind: 'archived' };
  if (raw === 'scope:global') return { kind: 'scope', scope: 'global' };
  if (raw === 'scope:custom') return { kind: 'scope', scope: 'custom' };
  const [prefix, value] = raw.split(':', 2);
  if (prefix === 'product' && value && VALID_REPORT_TYPES.has(value)) {
    return { kind: 'product', product: value };
  }
  if (prefix === 'company' && value) {
    return { kind: 'company', companyId: value };
  }
  if (prefix === 'folder' && value) {
    return { kind: 'folder', folderId: value };
  }
  return { kind: 'all' };
}

export function serializeRail(selection: RailSelection): string {
  switch (selection.kind) {
    case 'all':
      return 'all';
    case 'mine':
      return 'mine';
    case 'archived':
      return 'archived';
    case 'scope':
      return `scope:${selection.scope}`;
    case 'product':
      return `product:${selection.product}`;
    case 'company':
      return `company:${selection.companyId}`;
    case 'folder':
      return `folder:${selection.folderId}`;
  }
}

/**
 * Reports the current admin may see at all: builder-supported product types,
 * with private reports limited to their creator. (The API already enforces
 * this; the client re-enforces defensively — lifted from the legacy listing.)
 */
export function visibleToAdmin<T extends FilterableReport>(
  reports: T[],
  currentUserId: string | number | null | undefined
): T[] {
  return reports.filter(
    (r) =>
      VALID_REPORT_TYPES.has(r.reportType) &&
      (!r.isPrivate || String(r.createdBy) === String(currentUserId))
  );
}

/** The company a report is scoped to (custom-partner reports), if any. */
export function companyIdFor(report: FilterableReport): string | null {
  const companyId = report.searchConfig?.['companyId'] || report.metadata?.publishedCompanyId;
  return companyId ? String(companyId) : null;
}

/** Product bucket for a report; unknown types land in Core (mirrors legacy grouping). */
export function productForReport(report: FilterableReport): ProductCode {
  const rawType = (report.reportType || '').toUpperCase() as ProductCode;
  return PRODUCT_LABELS[rawType] ? rawType : 'Core';
}

export function isOwnPrivate(
  report: FilterableReport,
  currentUserId: string | number | null | undefined
): boolean {
  return report.isPrivate && String(report.createdBy) === String(currentUserId);
}

/**
 * Apply a rail selection. Archived reports appear ONLY under the `archived`
 * rail; product and company selections exclude private reports (they surface
 * under My Reports and All Reports instead).
 */
export function applyRail<T extends FilterableReport>(
  reports: T[],
  selection: RailSelection,
  currentUserId: string | number | null | undefined,
  /** reportId -> folderId (personal folders); only folder selections read it. */
  folderAssignments?: Record<string, string>
): T[] {
  if (selection.kind === 'archived') {
    return reports.filter((r) => r.isArchived);
  }
  const active = reports.filter((r) => !r.isArchived);
  switch (selection.kind) {
    case 'all':
      return active;
    case 'mine':
      return active.filter((r) => isOwnPrivate(r, currentUserId));
    case 'scope':
      return selection.scope === 'global'
        ? active.filter((r) => r.isGlobal)
        : active.filter((r) => r.isPartnerShared);
    case 'product':
      return active.filter((r) => !r.isPrivate && r.reportType === selection.product);
    case 'company':
      return active.filter((r) => !r.isPrivate && companyIdFor(r) === selection.companyId);
    case 'folder':
      return active.filter((r) => folderAssignments?.[String(r.id)] === selection.folderId);
  }
}

export function applyPublish<T extends FilterableReport>(
  reports: T[],
  publish: PublishFilter
): T[] {
  if (publish === 'published') return reports.filter((r) => r.isPublished);
  if (publish === 'unpublished') return reports.filter((r) => r.isUnpublished);
  return reports;
}

/**
 * Live-search matcher: case-insensitive substring across name, description,
 * product label, and (when provided) the report's company name.
 */
export function matchesSearch(
  report: FilterableReport,
  query: string,
  companyName?: string
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    report.name,
    report.description ?? '',
    PRODUCT_LABELS[productForReport(report)],
    companyName ?? '',
  ]
    .join('\n')
    .toLowerCase();
  return haystack.includes(q);
}

export interface RailCounts {
  all: number;
  mine: number;
  global: number;
  custom: number;
  archived: number;
  byProduct: Record<string, number>;
  byCompany: Record<string, number>;
  byFolder: Record<string, number>;
}

/**
 * Counts for every rail item / Browse All chip, computed with the same rules
 * as applyRail so labels always agree with the filtered result.
 */
export function railCounts(
  reports: FilterableReport[],
  currentUserId: string | number | null | undefined,
  /** reportId -> folderId (personal folders); omit to skip folder counts. */
  folderAssignments?: Record<string, string>
): RailCounts {
  const counts: RailCounts = {
    all: 0,
    mine: 0,
    global: 0,
    custom: 0,
    archived: 0,
    byProduct: {},
    byCompany: {},
    byFolder: {},
  };
  for (const report of reports) {
    if (report.isArchived) {
      counts.archived += 1;
      continue;
    }
    counts.all += 1;
    if (isOwnPrivate(report, currentUserId)) counts.mine += 1;
    if (report.isGlobal) counts.global += 1;
    if (report.isPartnerShared) counts.custom += 1;
    const folderId = folderAssignments?.[String(report.id)];
    if (folderId) {
      counts.byFolder[folderId] = (counts.byFolder[folderId] ?? 0) + 1;
    }
    if (!report.isPrivate) {
      counts.byProduct[report.reportType] = (counts.byProduct[report.reportType] ?? 0) + 1;
      const companyId = companyIdFor(report);
      if (companyId) {
        counts.byCompany[companyId] = (counts.byCompany[companyId] ?? 0) + 1;
      }
    }
  }
  return counts;
}
